import React, { useEffect, useMemo, useState } from "react";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Checkbox,
  ListItemText,
  Autocomplete,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createUser, getRoles, getNetworkPartners, getCofUsers } from "redux/actions";
import { AppState } from "redux/store";
import { AxiosResponse } from "axios";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
const PERMISSION_OPTIONS = [
  { value: "report_download", label: "Report Download" },
  { value: "generate_lead", label: "Generate Lead" },
];
const UserCreate = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const roles = useSelector((state: AppState) => state.master.roles || []);
  const networkPartners = useSelector((state: AppState) => state.master.networkPartners || []);
  const cofUsers = useSelector((state: AppState) => state.master.cofUsers || []);


  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    dispatch(getRoles());
    dispatch(getNetworkPartners());
    dispatch(getCofUsers());
  }, [dispatch]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    username: Yup.string().required("Username is required"),
    roleId: Yup.number().required("Role is required"),
    SRCode: Yup.string().when("roleId", {
      is: (roleId: number) => {
        const selectedRole = roles.find((r) => r.id === Number(roleId));
        return selectedRole?.role === "COF Admin" || selectedRole?.role === "COF User";
      },
      then: (schema) => schema.required("SR Code is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    contactNo: Yup.string()
      .matches(/^\d{10}$/, "Contact Number must be exactly 10 digits")
      .required("Contact Number is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    status: Yup.string()
      .oneOf(["true", "false"], "Status must be Active or Inactive")
      .required("Status is required"),
    password: Yup.string()
      .required("Password is required")
      .min(6, "Password must be at least 6 characters"),
    confirmPassword: Yup.string()
      .required("Confirm Password is required")
      .oneOf([Yup.ref("password")], "Passwords must match"),
    managerId: Yup.number().when("roleId", {
      is: (roleId: number) => {
        const selectedRole = roles.find((r) => r.id === roleId);
        return selectedRole?.role === "COF User";
      },
      then: (schema) => schema.moreThan(0, "Manager is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const handleSubmit = async (values: any) => {

    const dataPayload = {
      name: values.name,
      username: values.username,
      networkPartnerCode: values.network_partners.join(","),
      roleId: +values.roleId,
      contactNo: values.contactNo,
      email: values.email,
      password: values.password,
      IsActive: values.status === "true",
      SRCode: values.SRCode,
      managerId: values.managerId,
      permission: values.permissions.map((p) => p.trim()).join(","),
    };

    try {
      const response = (await dispatch(createUser(dataPayload))) as unknown as AxiosResponse;
      if (response.status === 200) {
        navigate(`/master/manageUser`);
      }
    } catch (error) {
      console.error("Error creating user:", error);
    }
  };
  const partnerCodeToName = useMemo(() => {
    const m: Record<string, string> = {};
    networkPartners.forEach((p: any) => (m[p.partnerCode] = (p.partnerName || '').trim()));
    return m;
  }, [networkPartners]);

  return (
    <Box sx={{ height: "100%", width: "100%", m: 8 }}>
      <Grid container direction="column" spacing={3} m={2}>
        <Formik
          initialValues={{
            name: "",
            username: "",
            roleId: "",
            contactNo: "",
            email: "",
            network_partners: [],
            status: "true",
            password: "",
            confirmPassword: "",
            SRCode: "",
            managerId: 0,
            permissions: []
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ touched, errors, values, setFieldValue }) => {

            const selectedRole = roles.find((r) => r.id === Number(values.roleId));
            const showNetworkPartner = selectedRole?.moduleType === "COF";
            const showManager = selectedRole?.role === "COF User";

            return (
              <Form>
                <Grid container spacing={3} mb={3}>
                  <Grid item xs={6}>
                    <Field
                      as={TextField}
                      fullWidth
                      label="Name"
                      name="name"
                      variant="outlined"
                      error={touched.name && !!errors.name}
                      helperText={<ErrorMessage name="name" />}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Field
                      as={TextField}
                      fullWidth
                      label="Username"
                      name="username"
                      variant="outlined"
                      error={touched.username && !!errors.username}
                      helperText={<ErrorMessage name="username" />}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={3} mb={3}>
                  <Grid item xs={6}>
                    <Field name="roleId">
                      {({ field }: any) => (
                        <TextField
                          {...field}
                          select
                          fullWidth
                          label="Role Type"
                          variant="outlined"
                          error={touched.roleId && !!errors.roleId}
                          helperText={<ErrorMessage name="roleId" />}
                        >
                          {roles.map((role) => (
                            <MenuItem key={role.id} value={role.id}>
                              {role.role}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    </Field>
                  </Grid>

                  <Grid item xs={6}>
                    <Field
                      as={TextField}
                      fullWidth
                      label="Email"
                      name="email"
                      variant="outlined"
                      error={touched.email && !!errors.email}
                      helperText={<ErrorMessage name="email" />}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={3} mb={3}>
                  <Grid item xs={6}>
                    <Field
                      as={TextField}
                      fullWidth
                      label="Password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      variant="outlined"
                      error={touched.password && !!errors.password}
                      helperText={<ErrorMessage name="password" />}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Field
                      as={TextField}
                      fullWidth
                      label="Confirm Password"
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      variant="outlined"
                      error={touched.confirmPassword && !!errors.confirmPassword}
                      helperText={<ErrorMessage name="confirmPassword" />}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowConfirmPassword((prev) => !prev)}
                              edge="end"
                            >
                              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={3} mb={3}>
                  <Grid item xs={6}>
                    <Field
                      as={TextField}
                      fullWidth
                      label="Contact Number"
                      name="contactNo"
                      variant="outlined"
                      inputProps={{ maxLength: 10 }}
                      error={touched.contactNo && !!errors.contactNo}
                      helperText={<ErrorMessage name="contactNo" />}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Field
                      as={TextField}
                      fullWidth
                      label="SR Code"
                      name="SRCode"
                      variant="outlined"
                      error={touched.SRCode && !!errors.SRCode}
                      helperText={<ErrorMessage name="SRCode" />}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Field name="status">
                      {({ field }: any) => (
                        <TextField
                          {...field}
                          select
                          fullWidth
                          label="Status"
                          variant="outlined"
                          error={touched.status && !!errors.status}
                          helperText={<ErrorMessage name="status" />}
                        >
                          <MenuItem value="true">Active</MenuItem>
                          <MenuItem value="false">Inactive</MenuItem>
                        </TextField>
                      )}
                    </Field>
                  </Grid>
                </Grid>

                {showNetworkPartner && (
                  <Grid container spacing={3} mb={3}>
                    <Grid item xs={4}>
                      <Field name="network_partners">
                        {({ field, form: { setFieldValue }, meta: { touched, error } }: any) => (
                          <Autocomplete
                            multiple
                            disableCloseOnSelect
                            options={networkPartners}
                            getOptionLabel={(option: any) =>
                              `${option.partnerName?.trim() || ''} - ${option.partnerCode}`
                            }
                            value={networkPartners.filter((partner: any) =>
                              Array.isArray(field.value) && field.value.includes(partner.partnerCode)
                            )}
                            onChange={(event, selected: any[]) => {
                              const selectedCodes = selected.map((item) => item.partnerCode);
                              setFieldValue('network_partners', selectedCodes);
                            }}
                            isOptionEqualToValue={(option, value) =>
                              option.partnerCode === value.partnerCode
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Network Partners"
                                error={touched.network_partners && !!error}
                                helperText={<ErrorMessage name="network_partners" />}
                              />
                            )}
                          />
                        )}
                      </Field>
                    </Grid>

                    <Grid item xs={4}>
                      <Field name="permissions">
                        {({ field }: any) => (
                          <TextField
                            {...field}
                            fullWidth
                            select
                            label="Permissions"
                            value={values.permissions}
                            onChange={(e) => setFieldValue("permissions", e.target.value)}
                            error={touched.permissions && !!errors.permissions}
                            helperText={<ErrorMessage name="permissions" />}
                            SelectProps={{
                              multiple: true,
                              renderValue: (selected) => (selected as string[]).join(", "),
                              MenuProps: {
                                PaperProps: {
                                  style: {
                                    maxHeight: 300,
                                  },
                                },
                              },
                            }}
                          >
                            {PERMISSION_OPTIONS.map((permission) => {
                              const trimmedName = permission.label.trim();
                              return (
                                <MenuItem key={permission.label} value={permission.label}>
                                  <Checkbox checked={values.permissions.includes(trimmedName)} />
                                  <ListItemText primary={trimmedName} />
                                </MenuItem>
                              );
                            })}
                          </TextField>
                        )}
                      </Field>
                    </Grid>

                    {showManager && (
                      <Grid item xs={4}>
                        <Field name="managerId">
                          {({ field, form, meta }: any) => (
                            <Autocomplete
                              options={cofUsers}
                              getOptionLabel={(option: any) => option?.name?.trim() || ""}
                              value={
                                cofUsers.find((user: any) => user.id === form.values.managerId) || null
                              }
                              onChange={(event, newValue) => {
                                form.setFieldValue("managerId", newValue ? newValue.id : 0);
                              }}
                              isOptionEqualToValue={(option, value) => option.id === value.id}

                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Manager"
                                  variant="outlined"
                                  error={meta.touched && Boolean(meta.error)}
                                  helperText={meta.touched && meta.error ? meta.error : ""}
                                />
                              )}
                            />
                          )}
                        </Field>
                      </Grid>
                    )}
                  </Grid>
                )}
                <Box
                  sx={{
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    width: "100%",
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                    py: 2,
                    px: 4,
                    zIndex: 1000,
                    backgroundColor: "white",
                    boxShadow: "0 -2px 10px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <Button variant="outlined" onClick={() => navigate("/master/manageUser/users")}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained" color="primary">
                    Add User
                  </Button>
                </Box>
              </Form>
            );
          }}
        </Formik>
      </Grid>
    </Box>
  );
};

export default UserCreate;
